'use client';

// Package Imports
import React from 'react';
import type { MouseEvent } from 'react';
import classNames from 'classnames';

// Style Imports
import styles from './stylizedButtonLink.module.scss';

interface StylizedButtonLinkProps {
  children: React.ReactNode;
  className?: string;
  href?: string;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  size?: 'mini' | 'small' | 'medium' | 'large' | 'wide';
  target?: '' | '_blank' | '_self';
  variant?: 'primary' | 'secondary' | 'tertiary' | 'neutral' | 'neon-pill';
  disabled?: boolean;
}

/**
 * A stylized link button component.
 *
 * @param children - The content to be displayed inside the button.
 * @param href - The URL to navigate to when the button is clicked.
 * @param onClick - The click handler function.
 * @param size - The size of the button.
 * @param target - The target window for the link.
 * @param variant - The variant/style of the button.
 * @param className - Additional CSS classes to apply to the button.
 * @param disabled - If true, applies a disabled class and prevents clicking.
 */
const StylizedButtonLink: React.FC<StylizedButtonLinkProps> = ({
  children,
  href = '',
  onClick,
  variant = 'primary',
  size = 'large',
  target = '',
  className,
  disabled = false,
  ...props
}) => {
  const classes = [styles.button, className];
  classes.push(styles[variant]);
  classes.push(styles[size]);

  if (disabled) {
    classes.push(styles.disabled);
  }

  return (
    <a
      href={!disabled ? href || undefined : undefined} // Prevent navigation if disabled
      onClick={!disabled && onClick ? (e) => onClick(e) : undefined} // Prevent click if disabled
      className={classNames(classes)}
      target={!disabled ? target || undefined : undefined}
      tabIndex={0}
      aria-disabled={disabled} // Adds accessibility support
      {...props}
    >
      {children}
    </a>
  );
};

export default StylizedButtonLink;
